import React, { useState } from "react";
import { Search, Package, Scissors } from "lucide-react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatPrice, formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

const STAGE_KEYS = ["commande", "mesures", "coupe", "couture", "finitions", "pret", "livre"];

function stageIndex(stage) {
    const i = STAGE_KEYS.indexOf(stage);
    return i === -1 ? 0 : i;
}

export default function CoutureTracking() {
    const { t } = useI18n();
    const [code, setCode] = useState("");
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const search = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setBusy(true);
        setError("");
        setOrder(null);
        try {
            const { data } = await api.get(`/couture/track/${encodeURIComponent(code.trim())}`);
            setOrder(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const current = order ? stageIndex(order.stage) : -1;

    return (
        <div className="min-h-screen bg-brand-paper flex flex-col">
            <Header />
            <main className="flex-1 px-4 py-16">
                <div className="max-w-2xl mx-auto" data-testid="couture-tracking-page">
                    <div className="text-center mb-10">
                        <div className="inline-flex w-14 h-14 bg-brand-gold items-center justify-center mb-5">
                            <Scissors className="w-7 h-7 text-brand-navy" />
                        </div>
                        <h1 className="font-display text-4xl text-brand-navy">{t("couture.track_title")}</h1>
                        <p className="text-gray-500 mt-2">{t("couture.track_sub")}</p>
                    </div>

                    <form onSubmit={search} className="flex gap-2 mb-10">
                        <input
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={t("couture.track_placeholder")}
                            data-testid="couture-track-input"
                            className="flex-1 bg-white border border-gray-200 px-4 py-3 text-sm font-mono uppercase focus:outline-none focus:border-brand-gold transition"
                        />
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="couture-track-submit"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-navy hover:bg-brand-gold text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                        >
                            <Search className="w-4 h-4" /> {t("couture.track_button")}
                        </button>
                    </form>

                    {error && (
                        <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-5 py-4 mb-8" data-testid="couture-track-error">
                            {error}
                        </div>
                    )}

                    {order && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="couture-track-result">
                            <div className="bg-white border border-gray-100 p-6 md:p-8 mb-6">
                                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                                    <div>
                                        <div className="text-[0.62rem] tracking-[0.2em] uppercase text-gray-400 mb-1">{t("couture.tracking_code")}</div>
                                        <div className="font-mono text-brand-gold-deep font-bold">{order.tracking_code}</div>
                                    </div>
                                    <span className="inline-block px-3 py-1.5 text-[0.62rem] tracking-[0.14em] uppercase font-semibold bg-brand-navy text-brand-gold">
                                        {t(`couture.status_${order.status}`)}
                                    </span>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{t("couture.client")}</div>
                                        <div className="font-semibold text-brand-navy">{order.client_name}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{t("couture.category")}</div>
                                        <div className="text-brand-navy">
                                            {order.category_name || "—"}
                                            {order.subcategory_name ? ` / ${order.subcategory_name}` : ""}
                                        </div>
                                    </div>
                                    {order.description && (
                                        <div className="sm:col-span-2">
                                            <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{t("couture.description")}</div>
                                            <div className="text-brand-navy">{order.description}</div>
                                        </div>
                                    )}
                                </div>

                                {/* Stage timeline */}
                                <div className="mt-8" data-testid="couture-track-timeline">
                                    <div className="flex items-center">
                                        {STAGE_KEYS.map((s, i) => {
                                            const done = i <= current;
                                            return (
                                                <React.Fragment key={s}>
                                                    <div className="flex flex-col items-center">
                                                        <div
                                                            className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                                                                done ? "bg-brand-gold border-brand-gold text-white" : "bg-white border-gray-200 text-gray-300"
                                                            }`}
                                                        >
                                                            <Package className="w-3.5 h-3.5" />
                                                        </div>
                                                        <div className={`mt-2 text-[0.55rem] tracking-[0.1em] uppercase text-center ${done ? "text-brand-navy font-semibold" : "text-gray-400"}`}>
                                                            {t(`couture.stage_${s}`)}
                                                        </div>
                                                    </div>
                                                    {i < STAGE_KEYS.length - 1 && (
                                                        <div className={`flex-1 h-0.5 mx-1 mb-5 ${i < current ? "bg-brand-gold" : "bg-gray-200"}`} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Payment */}
                                <div className="mt-8 grid sm:grid-cols-3 gap-4 border-t border-gray-100 pt-6">
                                    <div>
                                        <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{t("couture.price")}</div>
                                        <div className="font-display text-lg text-brand-navy">{formatPrice(order.price_fcfa, "FCFA")}</div>
                                        {order.price_eur != null && (
                                            <div className="text-xs text-gray-400">{formatPrice(order.price_eur, "EUR")}</div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{t("couture.deposit")}</div>
                                        <div className="font-display text-lg text-emerald-600">{formatPrice(order.deposit_fcfa, "FCFA")}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">{t("couture.balance")}</div>
                                        <div className="font-display text-lg text-brand-gold-deep">{formatPrice(order.balance_fcfa, "FCFA")}</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
